import React, { useEffect, useState } from 'react'

import { isNumber, mergeWith } from 'lodash'
import moment from 'moment'

import { Box, Card, Flex, Header, Icon, styled, Text } from '@driftt/dds'
import { faCalendarCheck } from '@driftt/dds.icons/light/faCalendarCheck'
import { faCirclePlay } from '@driftt/dds.icons/light/faCirclePlay'
import { faComment } from '@driftt/dds.icons/light/faComment'
import { faEye } from '@driftt/dds.icons/light/faEye'

import { fetchAccountEngagementSummary } from 'api/AccountActivityAPI'


const StatNumber = styled(Header, {
  textAlign: 'center',
  marginBottom: 0
})

const StatLabel = styled(Text, {
  variants: {
    variant: {
      body: {
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '$half'
      }
    }
  }
})

const StatContainer = styled(Box, {
  width: '100%',
  textAlign: 'center',
  border: '1px solid $gray4',
  borderRadius: '$2',
  padding: '$1 0'
})

const StatIcon = styled(Icon, {
  color: 'white',
  padding: '3px',
  borderRadius: '50%',
  fontSize: '$2'
})

export interface AccountActivityStatsProps {
  accountId: string
}

const AccountActivityStats = ({ accountId }: AccountActivityStatsProps) => {
  const [engagementSummary, setEngagementSummary] = useState<Record<string, number | undefined>>({})
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    fetchAccountEngagementSummary(
      accountId,
      moment()
        .subtract(1, 'month')
        .startOf('day')
        .valueOf(),
      moment()
        .endOf('day')
        .valueOf(),
      false
    ).then(data => {
      const formattedStats = data.engagementByWeek.reduce((acc: any, curr: any) => {
        const { engagementByType } = curr
        return mergeWith(acc, engagementByType, (objValue: any, srcValue: any) =>
          isNumber(objValue) ? objValue + srcValue : srcValue
        )
      }, {})
      setEngagementSummary(formattedStats)
      setLoading(false)
    })
  }, [accountId, setLoading])

  const siteVisits = loading ? '-' : engagementSummary['Site visit'] || 0
  const videoViews = loading ? '-' : engagementSummary['Drift Video view'] || 0
  const chatMessages = loading ? '-' : engagementSummary['Drift chat'] || 0
  const meetingsBookings = loading ? '-' : engagementSummary['Meeting booked'] || 0

  return (
    <Card
      css={{
        padding: '$2',
        marginBottom: '$2'
      }}>
      <Flex direction="column">
        <Header variant="h4">Activity this month</Header>
        <Flex css={{ width: '100%' }} justifyContent="space-between">
          <StatContainer>
            <StatLabel variant="body">
              <StatIcon
                size="extra-small"
                css={{
                  backgroundColor: '#24b232'
                }}
                icon={faEye}
              />{' '}
              Site visits
            </StatLabel>
            <StatNumber>{siteVisits}</StatNumber>
          </StatContainer>
          <StatContainer>
            <StatLabel variant="body">
              <StatIcon
                size="extra-small"
                css={{
                  backgroundColor: '#5196ed'
                }}
                icon={faComment}
              />{' '}
              Chats
            </StatLabel>
            <StatNumber>{chatMessages}</StatNumber>
          </StatContainer>
        </Flex>
        <Flex css={{ width: '100%' }} justifyContent="space-between">
          <StatContainer>
            <StatLabel variant="body">
              <StatIcon
                size="extra-small"
                css={{
                  backgroundColor: '#f05b4d'
                }}
                icon={faCalendarCheck}
              />{' '}
              Meetings booked
            </StatLabel>
            <StatNumber>{meetingsBookings}</StatNumber>
          </StatContainer>
          <StatContainer>
            <StatLabel variant="body">
              <StatIcon
                size="extra-small"
                css={{
                  backgroundColor: '#d8863d'
                }}
                icon={faCirclePlay}
              />{' '}
              Video views
            </StatLabel>
            <StatNumber>{videoViews}</StatNumber>
          </StatContainer>
        </Flex>
      </Flex>
    </Card>
  )
}

export default AccountActivityStats
